import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    Output,
    EventEmitter,
    ViewEncapsulation,
    OnInit,
} from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import {
    EstadoRegistro,
    RangoFechasRegistro_DTO,
    RegistroDetalleTiempo,
    ValidarRegistrosPorFechasDTO,
} from "../../../../../models/personal/registro-horas.model";
import moment from "moment";
import { RegistroHorasService } from "../../../../../services/employees/registro-horas.service";
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MapaGeolocalizacionComponent } from "../mapa-geolocalizacion/mapa-geolocalizacion.component";
import { MotivoDialogComponent } from "../motivo-dialog/motivo-dialog.component";
import { EditarRegistroModalComponent } from "../editar-registro-modal/editar-registro-modal.component";
import { HistorialRegistroModalComponent } from "../historial-registro-modal/historial-registro-modal.component";
import * as FileSaver from 'file-saver';

interface GrupoRegistroTiempo {
    titulo: string;
    totalTiempo: string;
    dias: DiaRegistroTiempo[];
}

interface DiaRegistroTiempo {
    fecha: Date;
    totalDia: string;
    registros: RegistroDetalleTiempo[];
}

@Component({
    selector: "app-grupo-registros-tiempo",
    templateUrl: "./grupo-registros-tiempo.component.html",
    styleUrls: ["./grupo-registros-tiempo.component.css"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class GrupoRegistrosTiempoComponent implements OnChanges, OnInit {
    @Input() empleado: number;
    @Input() titulo: string;
    @Input() registros: RegistroDetalleTiempo[] = [];
    @Input() formulario: boolean = false; // Indica si se debe presentar el formulario para seleccionar el rango de fechas.

    @Output() actualizarRegistros: EventEmitter<void> =
        new EventEmitter<void>();

    grupoRegistros: GrupoRegistroTiempo;
    formRangoFechas: UntypedFormGroup;
    maxDate: Date = new Date();

    constructor(
        private registroHorasService: RegistroHorasService,
        private fb: UntypedFormBuilder,
        protected snackBar: MatSnackBar,
        private dialog: MatDialog
    ) {}

    ngOnInit() {
        this.inicializarFormulario();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.registros && this.registros && this.registros.length > 0) {
            this.procesarRegistros();
        }
    }

    private inicializarFormulario() {
        this.formRangoFechas = this.fb.group({
            fechaInicio: ["", Validators.required],
            fechaFin: ["", Validators.required],
        });
    }

    buscarPorRango() {
        if (this.formRangoFechas.valid) {
            const fechaInicio = moment(
                this.formRangoFechas.get("fechaInicio").value
            ).startOf("day");
            const fechaFin = moment(
                this.formRangoFechas.get("fechaFin").value
            ).endOf("day");

            this.registroHorasService
                .getRegistrosHoras({
                    employeesId: this.empleado,
                    fechaInicio: fechaInicio.toDate(),
                    fechaFin: fechaFin.toDate(),
                    pageSize: 500,
                })
                .subscribe({
                    next: (response) => {
                        if (response && response.items) {
                            this.registros = response.items;
                            this.procesarRegistros();
                            this.actualizarRegistros.emit();

                            // En el caso de que no hayan items, se debe mostrar un mensaje de error
                            if (response.items.length === 0) {
                                this.snackBar.open(
                                    "No se encontraron registros para el rango de fechas seleccionado.",
                                    "Cerrar",
                                    {
                                        duration: 6000,
                                        panelClass: ["mat-toolbar", "mat-warn"],
                                    }
                                );
                            }
                        }
                    },
                });
        }
    }

    /**
     * Exporta los registros a un archivo Excel.
     */
    exportarExcel() {
        if (
            this.grupoRegistros.dias == null ||
            this.grupoRegistros.dias.length == 0
        ) {
            return;
        }

        const datos: RangoFechasRegistro_DTO = {
            inicio: moment(this.fechaMinima()).format("DD/MM/YYYY"),
            fin: moment(this.fechaMaxima()).format("DD/MM/YYYY"),
        };

        this.registroHorasService
            .informeHorasExcel(this.empleado, datos)
            .subscribe({
                next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    window.open(url, "_blank");
                },
            });
    }

    nombreEstado(estado: number): string {
        return EstadoRegistro[estado] || "Desconocido";
    }

    /**
     * Verifica si hay registros sin validar.
     * @returns true si hay registros sin validar, false en caso contrario.
     */
    tieneRegistrosSinValidar(): boolean {
        return this.registros.some(
            (registro) => registro.estado !== EstadoRegistro.Validado
        );
    }

    validarRegistro(registro: RegistroDetalleTiempo) {
        this.registroHorasService.validarRegistro(registro).subscribe({
            next: (resultado: RegistroDetalleTiempo) => {
                // Actualizar el registro en la lista de registros
                const index = this.registros.findIndex(
                    (r) => r.registroHorasId === registro.registroHorasId
                );
                if (index !== -1) {
                    this.registros[index] = resultado;
                }

                this.procesarRegistros();
                this.actualizarRegistros.emit();
            },
        });
    }

    /**
     * Valida todos los registros pendientes.
     */
    validarTodosPendientes() {
        // Confirmación de la acción
        const confirmacion = confirm(
            "¿Estás seguro de querer validar todos los registros pendientes para " +
                this.titulo.toLocaleLowerCase() +
                "?"
        );
        if (!confirmacion) return;

        const dto: ValidarRegistrosPorFechasDTO = {
            employeesId: this.empleado,
            fechaInicio: this.fechaMinima().toISOString(),
            fechaFin: this.fechaMaxima().toISOString(),
            userId: 0,
        };

        this.registroHorasService.validarRegistrosPendientes(dto).subscribe({
            next: (resultado: RegistroDetalleTiempo[]) => {
                this.registros = resultado;
                this.procesarRegistros();
                this.actualizarRegistros.emit();
            },
        });
    }

    /**
     * Rechaza un registro de horas, pidiendo antes el motivo obligatorio.
     */
    rechazarRegistro(registro: RegistroDetalleTiempo) {
        const dialogRef = this.dialog.open(MotivoDialogComponent, {
            width: "480px",
            maxWidth: "96vw",
            data: {
                titulo: "Rechazar registro",
                motivoLabel: "Motivo del rechazo",
            },
        });

        dialogRef.afterClosed().subscribe((motivo: string) => {
            if (!motivo) return;

            this.registroHorasService
                .rechazarRegistro(registro.registroHorasId, { motivo })
                .subscribe({
                    next: (resultado: RegistroDetalleTiempo) => {
                        this.reemplazarRegistro(resultado);
                    },
                });
        });
    }

    /**
     * Abre el diálogo de edición de un registro. El motivo es obligatorio.
     */
    editarRegistro(registro: RegistroDetalleTiempo) {
        const dialogRef = this.dialog.open(EditarRegistroModalComponent, {
            width: "480px",
            maxWidth: "96vw",
            data: { registro },
        });

        dialogRef.afterClosed().subscribe((resultado: RegistroDetalleTiempo) => {
            if (!resultado) return;
            this.reemplazarRegistro(resultado);
        });
    }

    /**
     * Muestra el histórico de auditoría de un registro.
     */
    verHistorial(registro: RegistroDetalleTiempo) {
        this.dialog.open(HistorialRegistroModalComponent, {
            width: "560px",
            maxWidth: "96vw",
            maxHeight: "80vh",
            data: { registroHorasId: registro.registroHorasId },
        });
    }

    /**
     * Exporta los registros a un archivo PDF.
     */
    exportarPdf() {
        if (this.grupoRegistros.dias == null || this.grupoRegistros.dias.length == 0) {
            return;
        }

        const datos: RangoFechasRegistro_DTO = {
            inicio: moment(this.fechaMinima()).format("DD/MM/YYYY"),
            fin: moment(this.fechaMaxima()).format("DD/MM/YYYY"),
        };

        this.registroHorasService.informeHorasPdf(this.empleado, datos).subscribe({
            next: (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                window.open(url, "_blank");
            },
        });
    }

    /**
     * Exporta los registros a un archivo CSV.
     */
    exportarCsv() {
        if (this.grupoRegistros.dias == null || this.grupoRegistros.dias.length == 0) {
            return;
        }

        const datos: RangoFechasRegistro_DTO = {
            inicio: moment(this.fechaMinima()).format("DD/MM/YYYY"),
            fin: moment(this.fechaMaxima()).format("DD/MM/YYYY"),
        };

        this.registroHorasService.informeHorasCsv(this.empleado, datos).subscribe({
            next: (blob: Blob) => {
                FileSaver.saveAs(blob, `InformeHoras_${this.empleado}.csv`);
            },
        });
    }

    /**
     * Reemplaza un registro actualizado en la lista y refresca la agrupación.
     */
    private reemplazarRegistro(resultado: RegistroDetalleTiempo) {
        const index = this.registros.findIndex(
            (r) => r.registroHorasId === resultado.registroHorasId
        );
        if (index !== -1) {
            this.registros[index] = resultado;
        }

        this.procesarRegistros();
        this.actualizarRegistros.emit();
    }

    // #region Métodos privados

    /**
     * Obtiene la fecha mínima de los registros.
     * @returns Fecha mínima.
     */
    private fechaMinima(): Date {
        return this.grupoRegistros.dias.reduce((min, dia) => {
            return dia.fecha < min ? dia.fecha : min;
        }, this.grupoRegistros.dias[0].fecha);
    }

    /**
     * Obtiene la fecha máxima de los registros.
     * @returns Fecha máxima.
     */
    private fechaMaxima(): Date {
        const fechaMaxima: Date = this.grupoRegistros.dias.reduce((max, dia) => {
            return dia.fecha > max ? dia.fecha : max;
        }, this.grupoRegistros.dias[0].fecha);

        // Para este caso, hay que sumarle un día a la fecha máxima
        fechaMaxima.setDate(fechaMaxima.getDate() + 1);

        return fechaMaxima;
    }

    /**
     * Procesa los registros y los agrupa por día.
     */
    private procesarRegistros() {
        const registrosPorDia = new Map<string, RegistroDetalleTiempo[]>();

        this.registros.forEach((registro) => {
            const fecha = new Date(registro.fecha).toDateString();
            if (!registrosPorDia.has(fecha)) {
                registrosPorDia.set(fecha, []);
            }
            registrosPorDia.get(fecha).push(registro);
        });

        const dias: DiaRegistroTiempo[] = Array.from(registrosPorDia.entries())
            .map(([fecha, registrosDia]) => ({
                fecha: new Date(fecha),
                totalDia: this.calcularTotalTiempo(registrosDia),
                registros: this.ordenarRegistros(registrosDia),
            }))
            .sort((a, b) => b.fecha.getTime() - a.fecha.getTime());

        this.grupoRegistros = {
            titulo: this.titulo,
            totalTiempo: this.calcularTotalTiempo(this.registros),
            dias: dias,
        };
    }

    /**
     * Ordena los registros por hora de entrada.
     * @param registros Lista de registros a ordenar.
     * @returns Lista de registros ordenados.
     */
    private ordenarRegistros(
        registros: RegistroDetalleTiempo[]
    ): RegistroDetalleTiempo[] {
        return [...registros].sort((a, b) => {
            const [horasA, minsA] = a.horaEntrada.split(":").map(Number);

            const [horasB, minsB] = b.horaEntrada.split(":").map(Number);
            const totalMinutosA = horasA * 60 + minsA;
            const totalMinutosB = horasB * 60 + minsB;
            return totalMinutosA - totalMinutosB;
        });
    }

    /**
     * Calcula el total de tiempo de los registros.
     * @param registros Lista de registros a calcular.
     * @returns Total de tiempo en formato HH:MM.
     */
    private calcularTotalTiempo(registros: RegistroDetalleTiempo[]): string {
        let totalMinutos = 0;

        registros.forEach((registro) => {
            let minutosTotales = 0;

            if (registro.duracion) {
                // Si ya tiene duración calculada, la usamos
                const [horas, minutos] = registro.duracion
                    .split(":")
                    .map(Number);
                minutosTotales = horas * 60 + minutos;
            } else if (registro.horaEntrada && registro.horaSalida) {
                // Si no tiene duración, la calculamos
                const [horasEntrada, minutosEntrada] = registro.horaEntrada
                    .split(":")
                    .map(Number);
                const [horasSalida, minutosSalida] = registro.horaSalida
                    .split(":")
                    .map(Number);

                if (
                    horasSalida < horasEntrada ||
                    (horasSalida === horasEntrada &&
                        minutosSalida < minutosEntrada)
                ) {
                    // Si la hora de salida es menor que la de entrada, significa que es del día siguiente
                    minutosTotales =
                        (24 - horasEntrada) * 60 -
                        minutosEntrada +
                        (horasSalida * 60 + minutosSalida);
                } else {
                    minutosTotales =
                        (horasSalida - horasEntrada) * 60 +
                        (minutosSalida - minutosEntrada);
                }
            }

            // Restar la duración de las pausas
            if (registro.pausas && registro.pausas.length > 0) {
                registro.pausas.forEach((pausa) => {
                    if (pausa.inicio && pausa.fin) {
                        const fechaInicio = moment(pausa.inicio);
                        const fechaFin = moment(pausa.fin);
                        const duracionPausa = fechaFin.diff(
                            fechaInicio,
                            "minutes"
                        );
                        minutosTotales -= duracionPausa;
                    }
                });
            }

            // Asegurarnos de que no tengamos valores negativos
            minutosTotales = Math.max(0, minutosTotales);

            // Guardamos la duración calculada en el registro
            const horas = Math.floor(minutosTotales / 60);
            const minutos = minutosTotales % 60;
            registro.duracion = `${horas.toString().padStart(2, "0")}:${minutos
                .toString()
                .padStart(2, "0")}`;

            totalMinutos += minutosTotales;
        });

        const horas = Math.floor(totalMinutos / 60);
        const minutos = totalMinutos % 60;
        return `${horas.toString().padStart(2, "0")}:${minutos
            .toString()
            .padStart(2, "0")}`;
    }

    // #region Métodos de geolocalización

    /**
     * Verifica si un registro tiene información de geolocalización.
     * @param registro Registro a verificar.
     * @returns true si tiene geolocalización, false en caso contrario.
     */
    tieneGeolocalizacion(registro: RegistroDetalleTiempo): boolean {
        return (
            !!(registro.latitudInicio && registro.longitudInicio) ||
            !!(registro.latitudFin && registro.longitudFin)
        );
    }

    /**
     * Muestra el mapa de geolocalización para un registro específico.
     * @param registro Registro del cual mostrar la geolocalización.
     */
    mostrarMapa(registro: RegistroDetalleTiempo) {
        if (!this.tieneGeolocalizacion(registro)) {
            this.snackBar.open(
                "Este registro no tiene información de geolocalización.",
                "Cerrar",
                {
                    duration: 3000,
                    panelClass: ["mat-toolbar", "mat-warn"],
                }
            );
            return;
        }

        const dialogRef = this.dialog.open(MapaGeolocalizacionComponent, {
            width: "900px",
            height: "600px",
            maxWidth: "90vw",
            maxHeight: "90vh",
            data: { registro: registro },
            disableClose: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            // El diálogo se cerró
        });
    }

    // #endregion

    /**
     * Redondeo bancario: los valores exactamente en medio se redondean al número par más cercano.
     * Este método es útil para evitar sesgos acumulativos en cálculos financieros.
     * @param valor Valor a redondear
     * @param decimales Número de decimales (por defecto 0 para minutos enteros)
     * @returns Valor redondeado
     * @example redonderoBancario(2.5, 0) → 2
     * @example redonderoBancario(3.5, 0) → 4
     */
    redonderoBancario(valor: number, decimales: number = 0): number {
        if (!valor || isNaN(valor)) return 0;
        
        const factor = Math.pow(10, decimales);
        const valorAjustado = valor * factor;
        const parteEntera = Math.floor(valorAjustado);
        const decimal = valorAjustado - parteEntera;

        if (decimal === 0.5) {
            // Si es exactamente .5, redondear al número par más cercano
            return parteEntera % 2 === 0 ? parteEntera / factor : (parteEntera + 1) / factor;
        }

        return Math.round(valorAjustado) / factor;
    }
  public trackByIndex(i: number) { return i; }

}
